import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { scrollToSection, telHref } from "@/lib/scroll";
import { useSettings } from "@/lib/SettingsContext";

const LINKS = [
  { label: "Services", hash: "#services" },
  { label: "Pricing", hash: "#pricing" },
  { label: "Our Standard", hash: "#manifesto" },
  { label: "Reviews", hash: "#reviews" },
  { label: "Careers", hash: "#careers" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const settings = useSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    scrollToSection(hash);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-[#FAF8F5]/85 shadow-[0_1px_0_#E5DEC3] backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <button
          data-testid="nav-brand"
          onClick={() => go("#top")}
          className="group flex items-baseline gap-1.5"
        >
          <span className="font-serif text-2xl font-medium tracking-tight text-[#0F382A]">
            Don Sparkle
          </span>
          <span className="h-2 w-2 rounded-full bg-[#D4AF37] transition-transform duration-300 group-hover:scale-150" />
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.hash)}
              className="group relative text-sm font-medium text-[#4A5A52] transition-colors duration-300 hover:text-[#0F382A]"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#D4AF37] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            data-testid="nav-phone-link"
            href={telHref(settings.phone_display)}
            className="flex items-center gap-2 text-sm font-semibold text-[#0F382A] transition-colors duration-300 hover:text-[#1B523F]"
          >
            <Phone className="h-4 w-4" strokeWidth={1.75} />
            {settings.phone_display}
          </a>
          <button
            data-testid="nav-book-button"
            onClick={() => go("#booking")}
            className="rounded-full bg-[#0F382A] px-6 py-2.5 text-sm font-semibold text-[#FAF8F5] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#1B523F]"
          >
            Book Now
          </button>
        </div>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(!open)}
          className="text-[#0F382A] lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-[#E5DEC3] bg-[#FAF8F5]/95 px-6 py-6 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col gap-4">
            {LINKS.map((l) => (
              <button
                key={l.hash}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => go(l.hash)}
                className="text-left font-serif text-xl text-[#0F382A]"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-book-button"
              onClick={() => go("#booking")}
              className="mt-2 rounded-full bg-[#0F382A] px-6 py-3 text-sm font-semibold text-[#FAF8F5]"
            >
              Book Now
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
}
